"use client";

import { track } from "@vercel/analytics";
import Link from "next/link";
import { useEffect, useState } from "react";

import type { OfferStoryblok } from "@/component-types-sb";
import { getOffers } from "@/lib/storyblok/getOffers";
import { isWithinStoryblokSchedule } from "@/lib/storyblok/utils";

type OfferTeaserProps = {
  campsiteId?: string;
  campsiteName?: string;
  count?: number;
  title?: string;
};

const OfferTeaser = ({
  campsiteId,
  count = 3,
  title = "Angebote",
}: OfferTeaserProps) => {
  const [offers, setOffers] = useState<OfferStoryblok[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const data = await getOffers({
          filter_query: {
            campsites: { in_array: campsiteId },
          },
          per_page: Math.max(count * 3, count + 3),
        });
        const filtered = data
          .filter((offer) =>
            isWithinStoryblokSchedule(
              offer.content?.publishDate,
              offer.content?.expireDate
            )
          )
          .slice(0, count);

        if (!isActive) return;
        setOffers(filtered);
      } catch (error) {
        console.error("Failed to load offers teaser", error);

        if (!isActive) return;
        setOffers([]);
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [campsiteId, count]);

  return (
    <div className="flex flex-col gap-2 mt-4">
      {isLoading && <Skeleton number={count} />}

      {!isLoading && offers.length > 0 && (
        <>
          <Link href="/veranstaltungen">
            <span className="font-semibold text-brand-primary100">{title}</span>
          </Link>
          <p className="bg-yellow-300/70 text-sm">
            Günstiger campen und Angebote entdecken!
          </p>
          <ul className="w-full text-sm">
            {offers?.map((blok) => (
              <li key={blok.id} className="px-2 py-2 border-b border-gray-200">
                <Link
                  href={blok.full_slug}
                  onClick={() => track("Event click")}
                  className="flex gap-2"
                >
                  {blok.name}
                </Link>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

const Skeleton = ({ number = 1 }) => (
  <>
    <div
      role="status"
      className="w-8/12 mb-1 h-6 bg-gray-200 animate-pulse"
    ></div>
    {Array(number)
      .fill(0)
      .map((_, index) => (
        <div
          key={index}
          className="w-12/12 my-1 h-4 bg-gray-200 animate-pulse"
        ></div>
      ))}
  </>
);

export { OfferTeaser };
